
/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../ui/tooltip";

interface TagLabelProps {
  labels: string[];
  maxVisible?: number;   // 👈 Controls how many chips to show before "+N"
}

const TagLabel: React.FC<TagLabelProps> = ({ labels, maxVisible }) => {

  if (!labels || labels.length === 0) {
    return (
      <div className="flex gap-1.5 flex-wrap">
        <span className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-normal text-foreground">
          None
        </span>
      </div>
    );
  }

  // When maxVisible is not provided → show ALL chips
  if (!maxVisible || labels.length <= maxVisible) {
    return (
      <div className="flex gap-1.5 flex-wrap">
        {labels.map((label, idx) => (
          <span
            key={idx}
            className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-normal text-foreground"
          >
            {label}
          </span>
        ))}
      </div>
    );
  }

  // Show the allowed number + Tooltip with remaining
  const visible = labels.slice(0, maxVisible);
  const remaining = labels.slice(maxVisible);

  // 👉 Sort remaining alphabetically for tooltip
  const sortedRemaining = [...remaining].sort((a, b) =>
    a.localeCompare(b) // handles A–Z, unicode, case-insensitive
  );

  return (
    <div className="flex gap-1.5 flex-wrap">
      {visible.map((label, idx) => (
        <span
          key={idx}
          className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-normal text-foreground"
        >
          {label}
        </span>
      ))}
      {remaining.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center rounded-md border border-border px-2 py-0.5 text-xs font-normal text-foreground cursor-pointer">
                +{remaining.length}
              </span>
            </TooltipTrigger>
            <TooltipContent className="whitespace-pre-line">
              {sortedRemaining.join("\n")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default TagLabel;
